import clsx from 'clsx'
import { useCallback, useEffect, useState, ReactNode } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'

import useWindowSize from '@/hooks/useWindowSize'

type Props = {
  title: string
  testimonials: CmpTestimonial[]
}

type CarouselProps = {
  slideClassName: string
  slides: ReactNode[]
  autoScroll?: boolean
}

let interval: NodeJS.Timeout
const AUTO_SCROLL_TIMER = 5000 // in ms

function Carousel({
  slideClassName,
  slides,
  autoScroll = true
}: CarouselProps): JSX.Element {
  const { isDesktop } = useWindowSize()
  const [carouselRef, carousel] = useEmblaCarousel({
    draggable: true,
    align: 'start',
    loop: true,
    slidesToScroll: isDesktop ? 2 : 1
  })
  const [, setActiveCarouselIndex] = useState(0)

  const scrollPrev = useCallback(
    () => carousel && carousel.scrollPrev(),
    [carousel]
  )

  const scrollNext = useCallback(
    () => carousel && carousel.scrollNext(),
    [carousel]
  )

  const onSelect = useCallback(() => {
    if (!carousel) return
    setActiveCarouselIndex(carousel.selectedScrollSnap())
  }, [carousel])

  useEffect(() => {
    if (!carousel) return
    carousel.on('select', onSelect)
  }, [carousel, onSelect])

  useEffect(() => {
    if (!autoScroll || !carousel) return

    interval = setInterval(() => carousel.scrollNext(), AUTO_SCROLL_TIMER)
    return () => clearInterval(interval)
  }, [carousel, autoScroll, onSelect])

  return (
    <div
      className="flex flex-col gap-y-8 lg:gap-y-20 | overflow-hidden"
      ref={carouselRef}
    >
      <div className="flex">
        {slides.map((slide, index) => {
          return (
            <div className={slideClassName} key={index}>
              {slide}
            </div>
          )
        })}
      </div>

      <div className={clsx('text-center', slides.length <= 2 && 'md:hidden')}>
        <button
          className="h-10 w-10 md:h-12 md:w-12 | bg-blue-20 | rounded-full | p-3 | fill-white | mr-7 md:mr-16"
          onClick={() => scrollPrev()}
          type="button"
        >
          <svg className="w-full h-full" viewBox="137.718 -1.001 366.563 644">
            <path d="M428.36 12.5c16.67-16.67 43.76-16.67 60.42 0 16.67 16.67 16.67 43.76 0 60.42L241.7 320c148.25 148.24 230.61 230.6 247.08 247.08 16.67 16.66 16.67 43.75 0 60.42-16.67 16.66-43.76 16.67-60.42 0-27.72-27.71-249.45-249.37-277.16-277.08a42.308 42.308 0 0 1-12.48-30.34c0-11.1 4.1-22.05 12.48-30.42C206.63 234.23 400.64 40.21 428.36 12.5z" />
          </svg>
        </button>
        <button
          className="h-10 w-10  md:h-12 md:w-12  | bg-blue-20 | rounded-full | p-3 | fill-white"
          onClick={() => scrollNext()}
        >
          <svg className="w-full h-full" viewBox="0 0 238.003 238.003">
            <path d="M181.776 107.719L78.705 4.648c-6.198-6.198-16.273-6.198-22.47 0s-6.198 16.273 0 22.47l91.883 91.883-91.883 91.883c-6.198 6.198-6.198 16.273 0 22.47s16.273 6.198 22.47 0l103.071-103.039a15.741 15.741 0 0 0 4.64-11.283c0-4.13-1.526-8.199-4.64-11.313z" />
          </svg>
        </button>
      </div>
    </div>
  )
}

function CmpTestimonial({ title, testimonials }: Props): JSX.Element {
  return (
    <section className="bg-white text-gray-90">
      <div className="container md:max-w-screen-2xl | mx-auto pl-4 lg:pl-20 py-14 md:py-22">
        <h3
          className="text-xl lg:text-4xl text-blue-web-70 font-heading text-center | max-w-col-8 | mb-14 lg:mb-16 mx-auto"
          dangerouslySetInnerHTML={{ __html: title }}
        />

        {testimonials.length > 0 ? (
          <Carousel
            slideClassName="min-w-fit  | px-3 lg:px-5"
            autoScroll={false}
            slides={testimonials.map(testimonial => (
              <div
                key={testimonial.id}
                className="flex flex-col justify-between | border-2 border-blue-20 rounded-lg | max-w-[20rem] lg:max-w-col-5 h-full | p-4 lg:py-8 lg:px-6"
              >
                <div className="flex gap-4 lg:gap-7 | mb-4">
                  {testimonial.authorImage ? (
                    <Image
                      className="rounded-lg | w-20 h-20 lg:w-44 lg:h-44 object-cover"
                      alt={testimonial.authorName}
                      src={testimonial.authorImage.data?.attributes.url ?? ''}
                      width={testimonial.authorImage.data?.attributes.width}
                      height={testimonial.authorImage.data?.attributes.height}
                    />
                  ) : null}
                  <div
                    className="text-xs lg:text-base"
                    dangerouslySetInnerHTML={{
                      __html: testimonial.review.replace(/```/g, '')
                    }}
                  />
                </div>
                <div className="flex items-center justify-between gap-3 lg:gap-6">
                  <div>
                    <p className="text-sm lg:text-lg font-medium | mb-0.5">
                      {testimonial.authorName}
                    </p>
                    <p className="text-caption lg:text-sm text-gray-80 line-clamp-2">
                      {testimonial.authorDesignation}
                    </p>
                  </div>

                  {testimonial.authorCompanyImage?.data && (
                    <Image
                      src={testimonial.authorCompanyImage.data.attributes.url}
                      className="max-h-5 lg:max-h-8 w-auto"
                      alt={
                        testimonial.authorCompanyImage.data.attributes
                          .alternativeText ?? ''
                      }
                      width={
                        testimonial.authorCompanyImage.data.attributes.width
                      }
                      height={
                        testimonial.authorCompanyImage.data.attributes.height
                      }
                    />
                  )}
                </div>
              </div>
            ))}
          />
        ) : null}
      </div>
    </section>
  )
}

export default CmpTestimonial
